import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, Palette as PaletteIcon } from '@mui/icons-material';
import { fetchBrandColors, assignBrandColor, clearBrandColorsCache, DEFAULT_PALETTE } from '../utils/brandColors';
import { fetchPillarColors, assignPillarColor, clearPillarColorsCache, DEFAULT_PILLAR_PALETTE } from '../utils/pillarColors';
import { useFilterContext } from '../context/FilterContext';
import { useAxiosClient } from '../utils/useAxiosClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  onColorsUpdated?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <>{children}</>
      )}
    </div>
  );
}

interface BrandColorsData {
  brand_color_mappings: { [key: string]: string };
  fallback_palette: string[];
  next_color_index: number;
}

interface PillarColorsData {
  pillar_color_mappings: { [key: string]: string };
  fallback_palette: string[];
  next_color_index: number;
}

interface Brand {
  id: string;
  name: string;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose, isDarkMode, onColorsUpdated }) => {
  const [tabValue, setTabValue] = useState(0);
  const [brandColorsData, setBrandColorsData] = useState<BrandColorsData | null>(null);
  const [pillarColorsData, setPillarColorsData] = useState<PillarColorsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingBrandColors, setEditingBrandColors] = useState<{ [brandName: string]: string }>({});
  const [editingPillarColors, setEditingPillarColors] = useState<{ [pillarName: string]: string }>({});

  const { availableBrands, availablePillars } = useFilterContext();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [pillars, setPillars] = useState<string[]>([]);
  const axiosClient = useAxiosClient();

  useEffect(() => {
    if (availableBrands) {
      setBrands(availableBrands as Brand[]);
    }
  }, [availableBrands]);

  useEffect(() => {
    if (availablePillars) {
      setPillars(availablePillars);
    }
  }, [availablePillars]);

  useEffect(() => {
    if (open) {
      loadBrandColorsAndInitialize();
      loadPillarColorsAndInitialize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brands, pillars]);

  const loadBrandColorsAndInitialize = async () => {
    if (brands.length === 0 && availableBrands && availableBrands.length > 0) {
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const colorsData = await fetchBrandColors(axiosClient);
      console.log('Fetched brand colors data:', colorsData);
      setBrandColorsData(colorsData);
      
      const initialEditingColors: { [brandName: string]: string } = {};
      if (brands.length > 0) {
        const localFallbackPalette = colorsData.fallback_palette || DEFAULT_PALETTE;
        const localNextColorIndex = colorsData.next_color_index || 0;
        let assignedFallbackCount = 0;

        brands.forEach((brand: Brand) => {
          const mappedColor = colorsData.brand_color_mappings[brand.name];
          if (mappedColor) {
            initialEditingColors[brand.name] = mappedColor;
            console.log(`Brand ${brand.name} has assigned color: ${mappedColor}`);
          } else {
            const colorIndex = (localNextColorIndex + assignedFallbackCount) % localFallbackPalette.length;
            initialEditingColors[brand.name] = localFallbackPalette[colorIndex] || DEFAULT_PALETTE[0];
            console.log(`Brand ${brand.name} using fallback color: ${initialEditingColors[brand.name]}`);
            assignedFallbackCount++;
          }
        });
      }
      setEditingBrandColors(initialEditingColors);
      console.log('Updated editing colors:', initialEditingColors);

    } catch (err) {
      setError('Failed to load brand colors data');
      console.error('Error loading brand colors data:', err);
      setBrandColorsData({
        brand_color_mappings: {},
        fallback_palette: DEFAULT_PALETTE,
        next_color_index: 0
      });
      const initialEditingColorsOnError: { [brandName: string]: string } = {};
      if (brands.length > 0) {
        brands.forEach((brand: Brand, index: number) => {
            initialEditingColorsOnError[brand.name] = DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
        });
      }
      setEditingBrandColors(initialEditingColorsOnError);
    } finally {
      setLoading(false);
    }
  };

  const loadPillarColorsAndInitialize = async () => {
    if (pillars.length === 0 && availablePillars && availablePillars.length > 0) {
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const colorsData = await fetchPillarColors(axiosClient);
      console.log('Fetched pillar colors data:', colorsData);
      setPillarColorsData(colorsData);
      
      const initialEditingColors: { [pillarName: string]: string } = {};
      if (pillars.length > 0) {
        const localFallbackPalette = colorsData.fallback_palette || DEFAULT_PILLAR_PALETTE;
        const localNextColorIndex = colorsData.next_color_index || 0;
        let assignedFallbackCount = 0;

        pillars.forEach((pillar: string) => {
          const mappedColor = colorsData.pillar_color_mappings[pillar];
          if (mappedColor) {
            initialEditingColors[pillar] = mappedColor;
            console.log(`Pillar ${pillar} has assigned color: ${mappedColor}`);
          } else {
            const colorIndex = (localNextColorIndex + assignedFallbackCount) % localFallbackPalette.length;
            initialEditingColors[pillar] = localFallbackPalette[colorIndex] || DEFAULT_PILLAR_PALETTE[0];
            console.log(`Pillar ${pillar} using fallback color: ${initialEditingColors[pillar]}`);
            assignedFallbackCount++;
          }
        });
      }
      setEditingPillarColors(initialEditingColors);
      console.log('Updated pillar editing colors:', initialEditingColors);

    } catch (err) {
      setError('Failed to load pillar colors data');
      console.error('Error loading pillar colors data:', err);
      setPillarColorsData({
        pillar_color_mappings: {},
        fallback_palette: DEFAULT_PILLAR_PALETTE,
        next_color_index: 0
      });
      const initialEditingColorsOnError: { [pillarName: string]: string } = {};
      if (pillars.length > 0) {
        pillars.forEach((pillar: string, index: number) => {
            initialEditingColorsOnError[pillar] = DEFAULT_PILLAR_PALETTE[index % DEFAULT_PILLAR_PALETTE.length];
        });
      }
      setEditingPillarColors(initialEditingColorsOnError);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleColorChange = (brandName: string, color: string) => {
    setEditingBrandColors(prev => ({ ...prev, [brandName]: color }));
  };

  const handlePillarColorChange = (pillarName: string, color: string) => {
    setEditingPillarColors(prev => ({ ...prev, [pillarName]: color }));
  };

  const getActualBrandColor = (brandName: string): string => {
    if (brandColorsData?.brand_color_mappings[brandName]) {
      const assignedColor = brandColorsData.brand_color_mappings[brandName];
      console.log(`getActualBrandColor: ${brandName} has assigned color: ${assignedColor}`);
      return assignedColor;
    }
    if (brandColorsData && brands.length > 0) {
      const localFallbackPalette = brandColorsData.fallback_palette || DEFAULT_PALETTE;
      // Use stable alphabetical indexing (same as getBrandColors function)
      const sortedBrands = [...brands].sort((a, b) => a.name.localeCompare(b.name));
      const brandIndex = sortedBrands.findIndex(brand => brand.name === brandName);
      if (brandIndex !== -1) {
        const fallbackColor = localFallbackPalette[brandIndex % localFallbackPalette.length] || DEFAULT_PALETTE[0];
        console.log(`getActualBrandColor: ${brandName} using stable fallback color at index ${brandIndex}: ${fallbackColor}`);
        return fallbackColor;
      }
    }
    console.log(`getActualBrandColor: ${brandName} using default fallback: ${DEFAULT_PALETTE[0]}`);
    return DEFAULT_PALETTE[0];
  };

  const getActualPillarColor = (pillarName: string): string => {
    if (pillarColorsData?.pillar_color_mappings[pillarName]) {
      const assignedColor = pillarColorsData.pillar_color_mappings[pillarName];
      console.log(`getActualPillarColor: ${pillarName} has assigned color: ${assignedColor}`);
      return assignedColor;
    }
    if (pillarColorsData && pillars.length > 0) {
      const localFallbackPalette = pillarColorsData.fallback_palette || DEFAULT_PILLAR_PALETTE;
      // Use stable alphabetical indexing (same as getPillarColors function)
      const sortedPillars = [...pillars].sort((a, b) => a.localeCompare(b));
      const pillarIndex = sortedPillars.findIndex(pillar => pillar === pillarName);
      if (pillarIndex !== -1) {
        const fallbackColor = localFallbackPalette[pillarIndex % localFallbackPalette.length] || DEFAULT_PILLAR_PALETTE[0];
        console.log(`getActualPillarColor: ${pillarName} using stable fallback color at index ${pillarIndex}: ${fallbackColor}`);
        return fallbackColor;
      }
    }
    console.log(`getActualPillarColor: ${pillarName} using default fallback: ${DEFAULT_PILLAR_PALETTE[0]}`);
    return DEFAULT_PILLAR_PALETTE[0];
  };

  const handleSaveAllColors = async () => {
    setSaving(true);
    setError(null);
    try {
      // Save all changed brand colors
      const brandPromises = Object.entries(editingBrandColors).map(([brandName, color]) => {
        const currentColor = getActualBrandColor(brandName);
        if (color !== currentColor) {
          return (assignBrandColor as any)(axiosClient, brandName, color);
        }
        return null;
      });

      // Save all changed pillar colors
      const pillarPromises = Object.entries(editingPillarColors).map(([pillarName, color]) => {
        const currentColor = getActualPillarColor(pillarName);
        if (color !== currentColor) {
          return (assignPillarColor as any)(axiosClient, pillarName, color);
        }
        return null;
      });

      // Filter out null promises and execute all updates
      const validBrandPromises = brandPromises.filter(p => p !== null);
      const validPillarPromises = pillarPromises.filter(p => p !== null);
      const allPromises = [...validBrandPromises, ...validPillarPromises];
      
      if (allPromises.length > 0) {
        await Promise.all(allPromises);
        
        // Clear caches and refresh data
        clearBrandColorsCache();
        clearPillarColorsCache();
        await loadBrandColorsAndInitialize();
        await loadPillarColorsAndInitialize();
        
        // Dispatch events to notify other components about color updates
        window.dispatchEvent(new CustomEvent('brandColorsUpdated'));
        window.dispatchEvent(new CustomEvent('pillarColorsUpdated'));
        
        // Notify parent that colors have been updated
        if (onColorsUpdated) {
          onColorsUpdated();
        }
      }
    } catch (err) {
      setError('Failed to save colors');
      console.error('Error saving colors:', err);
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for dummy chart data
  const generateDummyTimeData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => {
      const dataPoint: any = { month };
      brands.forEach(brand => {
        dataPoint[brand.name] = Math.floor(Math.random() * 100) + 20;
      });
      return dataPoint;
    });
  };

  const generateDummyBarData = () => {
    return brands.map(brand => ({
      name: brand.name,
      value: Math.floor(Math.random() * 100) + 20
    }));
  };

  // Check if there are any unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if ((!brandColorsData || brands.length === 0) && (!pillarColorsData || pillars.length === 0)) return false;
    
    // Check brand changes
    const hasBrandChanges = brands.some(brand => {
      const currentColor = getActualBrandColor(brand.name);
      const editingColor = editingBrandColors[brand.name];
      
      // If no editing color is set, there are no changes for this brand
      if (!editingColor) return false;
      
      // Compare the editing color with the current color
      return editingColor.toLowerCase() !== currentColor.toLowerCase();
    });

    // Check pillar changes
    const hasPillarChanges = pillars.some(pillar => {
      const currentColor = getActualPillarColor(pillar);
      const editingColor = editingPillarColors[pillar];
      
      // If no editing color is set, there are no changes for this pillar
      if (!editingColor) return false;
      
      // Compare the editing color with the current color
      return editingColor.toLowerCase() !== currentColor.toLowerCase();
    });

    return hasBrandChanges || hasPillarChanges;
  }, [editingBrandColors, editingPillarColors, brandColorsData, pillarColorsData, brands, pillars]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDarkMode ? 'grey.900' : 'background.paper',
          color: isDarkMode ? 'common.white' : 'text.primary'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px:2 }}>
        <Typography variant="h6">Settings</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{p:0}}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px:2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Theme & Colors" icon={<PaletteIcon />} sx={{py:0, minHeight: 48}}/>
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{p:2.5}}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {loading && !brandColorsData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            )}

            <Typography variant="h5" gutterBottom sx={{ mt: tabValue === 0 ? 0 : 2, mb: 1 }}>
              Brand Colors
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Change the display color for each brand. This affects all users of your organization.
            </Typography>
            
            {loading && brandColorsData && ( 
               <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb:1, height: 20 }}>
                  <CircularProgress size={20} /> 
                  <Typography variant="caption" sx={{ml:1}}>Updating...</Typography>
               </Box>
            )}
            {!loading && brandColorsData && <Box sx={{height: 20, mb:1}}/>}

            {brands.length === 0 && !loading && (
              <Typography>No brands found for this organization, or brand list is still loading.</Typography>
            )}

            {brandColorsData && brands.length > 0 && (
              <List sx={{ bgcolor: isDarkMode ? 'grey.800' : 'background.default', borderRadius: 1, p:0, overflow: 'hidden' }}>
                {brands.map((brand) => {
                  const actualColor = getActualBrandColor(brand.name);
                  const colorForPicker = editingBrandColors[brand.name] || actualColor;

                  return (
                    <ListItem 
                      key={brand.id} 
                      divider
                      sx={{ 
                          py: 1.5,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 2,
                          px: 2
                      }}
                    >
                      {/* Color picker button on the left */}
                      <Box sx={{ position: 'relative' }}>
                        <input 
                          type="color" 
                          aria-label={`Color picker for ${brand.name}`}
                          value={colorForPicker}
                          onChange={(e) => handleColorChange(brand.name, e.target.value)}
                          style={{ 
                              width: 40,
                              height: 40, 
                              border: 'none', 
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: colorForPicker,
                              padding: 0
                          }}
                        />
                      </Box>

                      {/* Brand name */}
                      <ListItemText 
                          primary={brand.name} 
                          sx={{ flexGrow: 1, textAlign: 'left' }} 
                      />

                      {/* Hex code display */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          minWidth: 80,
                          textAlign: 'right'
                        }}
                      >
                        {colorForPicker.toUpperCase()}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {/* Color Preview Charts */}
            {brandColorsData && brands.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Color Preview
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Preview how your brand colors will appear in charts
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Line Chart Preview */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: isDarkMode ? 'grey.800' : 'background.paper' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
                        DUMMY DATA - Line Chart Preview
                      </Typography>
                      <Box sx={{ height: 200, width: '100%' }}>
                        <ResponsiveContainer>
                          <LineChart data={generateDummyTimeData()}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            {brands.map((brand, index) => {
                              const color = editingBrandColors[brand.name] || getActualBrandColor(brand.name);
                              return (
                                <Line
                                  key={brand.id}
                                  type="monotone"
                                  dataKey={brand.name}
                                  stroke={color}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Bar Chart Preview */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: isDarkMode ? 'grey.800' : 'background.paper' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
                        DUMMY DATA - Bar Chart Preview
                      </Typography>
                      <Box sx={{ height: 200, width: '100%' }}>
                        <ResponsiveContainer>
                          <BarChart data={generateDummyBarData()} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={80} />
                            <Tooltip />
                            <Bar dataKey="value">
                              {generateDummyBarData().map((entry: { name: string; value: number }, index: number) => {
                                const brand = brands.find(b => b.name === entry.name);
                                const color = brand ? 
                                  (editingBrandColors[brand.name] || getActualBrandColor(brand.name)) : 
                                  DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Pillar Colors Section */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 1 }}>
              Pillar Colors
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Change the display color for each pillar. This affects all users of your organization.
            </Typography>
            
            {loading && pillarColorsData && ( 
               <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb:1, height: 20 }}>
                  <CircularProgress size={20} /> 
                  <Typography variant="caption" sx={{ml:1}}>Updating...</Typography>
               </Box>
            )}
            {!loading && pillarColorsData && <Box sx={{height: 20, mb:1}}/>}

            {pillars.length === 0 && !loading && (
              <Typography>No pillars found for this organization, or pillar list is still loading.</Typography>
            )}

            {pillarColorsData && pillars.length > 0 && (
              <List sx={{ bgcolor: isDarkMode ? 'grey.800' : 'background.default', borderRadius: 1, p:0, overflow: 'hidden' }}>
                {pillars.map((pillar) => {
                  const actualColor = getActualPillarColor(pillar);
                  const colorForPicker = editingPillarColors[pillar] || actualColor;

                  return (
                    <ListItem 
                      key={pillar} 
                      divider
                      sx={{ 
                          py: 1.5,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 2,
                          px: 2
                      }}
                    >
                      {/* Color picker button on the left */}
                      <Box sx={{ position: 'relative' }}>
                        <input 
                          type="color" 
                          aria-label={`Color picker for ${pillar}`}
                          value={colorForPicker}
                          onChange={(e) => handlePillarColorChange(pillar, e.target.value)}
                          style={{ 
                              width: 40,
                              height: 40, 
                              border: 'none', 
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: colorForPicker,
                              padding: 0
                          }}
                        />
                      </Box>

                      {/* Pillar name */}
                      <ListItemText 
                          primary={pillar} 
                          sx={{ flexGrow: 1, textAlign: 'left' }} 
                      />

                      {/* Hex code display */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          minWidth: 80,
                          textAlign: 'right'
                        }}
                      >
                        {colorForPicker.toUpperCase()}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {/* Pillar Color Preview Charts */}
            {pillarColorsData && pillars.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Pillar Color Preview
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Preview how your pillar colors will appear in charts
                </Typography>
                
                <Grid container spacing={3}>
                  {/* Pillar Bar Chart Preview */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: isDarkMode ? 'grey.800' : 'background.paper' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
                        DUMMY DATA - Pillar Bar Chart Preview
                      </Typography>
                      <Box sx={{ height: 200, width: '100%' }}>
                        <ResponsiveContainer>
                          <BarChart data={pillars.slice(0, 6).map(pillar => ({ name: pillar, value: Math.floor(Math.random() * 100) + 20 }))} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Bar dataKey="value">
                              {pillars.slice(0, 6).map((pillar: string, index: number) => {
                                const color = editingPillarColors[pillar] || getActualPillarColor(pillar);
                                return <Cell key={`cell-${index}`} fill={color} />;
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Pillar Line Chart Preview */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: isDarkMode ? 'grey.800' : 'background.paper' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
                        DUMMY DATA - Pillar Trend Preview
                      </Typography>
                      <Box sx={{ height: 200, width: '100%' }}>
                        <ResponsiveContainer>
                          <LineChart data={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map(month => {
                            const dataPoint: any = { month };
                            pillars.slice(0, 4).forEach(pillar => {
                              dataPoint[pillar] = Math.floor(Math.random() * 100) + 20;
                            });
                            return dataPoint;
                          })}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            {pillars.slice(0, 4).map((pillar, index) => {
                              const color = editingPillarColors[pillar] || getActualPillarColor(pillar);
                              return (
                                <Line
                                  key={pillar}
                                  type="monotone"
                                  dataKey={pillar}
                                  stroke={color}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{px:2, py:1.5, borderTop: 1, borderColor: 'divider'}}>
        <Button onClick={onClose}>Close</Button>
        <Button 
          onClick={handleSaveAllColors} 
          disabled={!hasUnsavedChanges || saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            minWidth: 100,
            opacity: (!hasUnsavedChanges || saving) ? 0.6 : 1,
            '&.Mui-disabled': {
              backgroundColor: 'grey.400',
              color: 'grey.600'
            }
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal; 